import {useEffect, useState} from 'react';
import type {SearchResultGroup} from 'src/api/ProactApiClient';
import {useLazyGlobalSearchQuery} from 'src/redux/queries/search';
import useDebounce from 'src/shared/hooks/useDebounce';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

export interface UseGlobalSearchResult {
  query: string;
  setQuery: (query: string) => void;
  groups: SearchResultGroup[];
  isEligible: boolean;
  isSearching: boolean;
}

export const useGlobalSearch = (): UseGlobalSearchResult => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);
  const [triggerSearch, {isFetching}] = useLazyGlobalSearchQuery();
  const [groups, setGroups] = useState<SearchResultGroup[]>([]);

  const trimmedQuery = debouncedQuery.trim();
  const isEligible = trimmedQuery.length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (!isEligible) {
      setGroups([]);
      return;
    }

    let isStale = false;

    triggerSearch({query: trimmedQuery})
      .unwrap()
      .then(response => {
        if (!isStale) {
          setGroups(response.groups ?? []);
        }
      })
      .catch(() => {
        if (!isStale) {
          setGroups([]);
        }
      });

    return () => {
      isStale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedQuery, isEligible]);

  return {
    query,
    setQuery,
    groups,
    isEligible,
    isSearching: isEligible && isFetching,
  };
};
